import { useMemo, useState } from 'react';
import { NavLink, useParams, Link } from 'react-router-dom';
import Section from '../components/Section';
import { IconArrowLeft } from '../components/Icon';

/**
 * Vpnee documentation hub. Slug-keyed article map, sidebar grouped by
 * category, in-page search across titles + summary keywords, and a
 * next/previous footer for reading top-to-bottom. Each article is
 * self-contained JSX — no CMS, no MDX, no runtime fetch.
 */
type Doc = {
  slug: string;
  title: string;
  summary: string;   // one-liner, indexed by the search box
  group: string;     // sidebar grouping
  body: JSX.Element;
};

const DOCS_LIST: Doc[] = [
  // ── Getting started ────────────────────────────────────────────────
  { slug: 'getting-started', group: 'Getting started', title: 'Getting started',
    summary: 'install, first launch, connect for the first time',
    body: (
      <div className="legal">
        <h2>Install</h2>
        <p>Grab the Android APK from <Link to="/download">the download page</Link> or Google Play. On Windows, run the signed <code>Vpnee_Setup_1.0.2.exe</code>. The install-from-unknown-sources prompt appears once on Android — allow it, then tap <strong>Install</strong>.</p>
        <h2>First launch</h2>
        <p>Accept the terms (once), create an account with email or Google. A VLESS UUID is generated server-side and stored on your device. No card, no email confirmation loop.</p>
        <h2>Connect</h2>
        <p>Tap the big power button. Grant the OS VPN permission the very first time. REALITY handshake completes in under 15 s on a normal network — you're routed immediately after.</p>
        <h2>Verify it works</h2>
        <p>Open <a href="https://ifconfig.io" target="_blank" rel="noopener">ifconfig.io</a> or <a href="https://icanhazip.com" target="_blank" rel="noopener">icanhazip.com</a> in a browser. The address should be the picked node's IP, not your ISP's. If they match, connection wasn't handed off — see <Link to="/docs/troubleshooting">Troubleshooting</Link>.</p>
      </div>
    ),
  },
  { slug: 'account-signup', group: 'Getting started', title: 'Create & manage account',
    summary: 'signup, google, facebook, sso, sign out, delete',
    body: (
      <div className="legal">
        <h2>Email + password</h2>
        <p>Username 3–24 chars, password minimum 8 chars including one digit. Email is optional but required for password recovery and payment receipts.</p>
        <h2>Google / Facebook sign-in</h2>
        <p>Tap the provider on the Account screen — the system browser opens for OAuth. On success you're handed back to the app via the <code>vpnee://</code> deep link. Provider email is stored, never the token; the token is single-use with a 10-minute TTL.</p>
        <h2>Sign out vs delete</h2>
        <p><strong>Sign out</strong> clears the local session token; your data and plan stay. <strong>Delete account</strong> schedules removal in 7 days — an active tunnel session cancels the delete. Cannot delete during an active pro plan until cancelled.</p>
      </div>
    ),
  },
  { slug: 'windows-client', group: 'Getting started', title: 'Windows client',
    summary: 'windows setup, driver, wintun, kill switch, tray',
    body: (
      <div className="legal">
        <h2>Installer</h2>
        <p>Windows 10 build 1809+ or Windows 11. The installer is signed with an EV certificate — no SmartScreen warning on first run. Requires admin elevation once (installs the Wintun driver).</p>
        <h2>Wintun driver</h2>
        <p>Vpnee ships the WireGuard-Foundation Wintun driver (identical build), placed under <code>C:\Program Files\Vpnee\bin\wintun</code>. Uninstalling Vpnee also uninstalls the driver.</p>
        <h2>Tray tile + hotkey</h2>
        <p>The system tray icon shows tunnel state at a glance (rose = up, dim = down). Double-click to open the panel, right-click for a quick disconnect. <code>Ctrl+Shift+V</code> global hotkey toggles the tunnel from anywhere.</p>
        <h2>Auto-start on boot</h2>
        <p>Settings → General → toggle <strong>Start with Windows</strong>. The service auto-connects to your last-used node.</p>
      </div>
    ),
  },
  { slug: 'linux-preview', group: 'Getting started', title: 'Linux (preview)',
    summary: 'appimage, deb, rpm, systemd, arch, tui',
    body: (
      <div className="legal">
        <h2>Preview builds</h2>
        <p>AppImage, <code>.deb</code>, and <code>.rpm</code> preview builds are on the download page. Glibc 2.31+ (Debian 11 / Ubuntu 22.04 / Fedora 36 / equivalents). systemd required for the auto-restart service unit.</p>
        <h2>Command line</h2>
        <p>The <code>vpneectl</code> binary drives the daemon. <code>vpneectl connect</code>, <code>vpneectl status</code>, <code>vpneectl servers</code>. Full flag list under <code>vpneectl --help</code>.</p>
        <h2>Systemd service</h2>
        <p>Enable at boot: <code>sudo systemctl enable --now vpnee</code>. Logs stream through journald (<code>journalctl -u vpnee -f</code>).</p>
        <h2>Not yet supported</h2>
        <p>musl-libc distros (Alpine), 32-bit ARM, and Snap/Flatpak sandboxed installs — the daemon needs raw <code>CAP_NET_ADMIN</code>.</p>
      </div>
    ),
  },
  { slug: 'ios-testflight', group: 'Getting started', title: 'iOS (TestFlight)',
    summary: 'ios, iphone, ipad, apple, testflight, network extension',
    body: (
      <div className="legal">
        <h2>Get on the TestFlight</h2>
        <p>The public TestFlight link is posted on <Link to="/download">the download page</Link> when the beta round opens. Slots are capped at 10,000 during testing.</p>
        <h2>Native NetworkExtension tunnel</h2>
        <p>iOS build ships a Packet-Tunnel Provider using the same Xray core as Android — real system-wide VPN, not a proxy hop. Works on iOS 15+.</p>
        <h2>Known limits during beta</h2>
        <p>Widgets require iOS 17+. Live Activity (Dynamic Island) is off during the beta and lands with the first production release. Split-tunnel per-app rules are not implemented on iOS — the whole device is tunneled or off.</p>
      </div>
    ),
  },

  // ── Networking & routing ───────────────────────────────────────────
  { slug: 'servers', group: 'Networking & routing', title: 'Servers & routing',
    summary: 'server picker, auto best, manual, anti-dpi, tls fragmentation',
    body: (
      <div className="legal">
        <h2>Auto (best node)</h2>
        <p>Default. The app health-probes every active server on connect and picks the lowest-latency one that isn't over its Xray placement quota. Recomputes on every reconnect.</p>
        <h2>Manual pick (Pro)</h2>
        <p>Servers tab → pick a city → tap <strong>Set Manual</strong>. The choice sticks across sessions until you clear it. Free tier can browse the list but not pin.</p>
        <h2>Anti-DPI toggle</h2>
        <p>Settings → Advanced → toggle <strong>TLS fragmentation</strong> for aggressive censors. Small throughput cost, big evasion benefit — splits the ClientHello across two packets so pattern-matching firewalls can't reassemble a signature.</p>
        <h2>Session pinning</h2>
        <p>Once connected, the app stays on that node until you disconnect or the node health-probe fails 3× in 45 s — you don't get shuffled to a different city mid-session.</p>
      </div>
    ),
  },
  { slug: 'reality-explained', group: 'Networking & routing', title: 'REALITY, in one paragraph',
    summary: 'reality, tls 1.3, handshake, ja3, sni, decoy',
    body: (
      <div className="legal">
        <h2>What REALITY actually does</h2>
        <p>REALITY reuses the TLS 1.3 handshake of a real decoy site (typically a major CDN). Your Vpnee client dials the decoy, completes the TLS handshake as if it were a legitimate visitor, then swaps to your VPN traffic under the same session. From the wire an inspector sees exactly what a real visit to that CDN looks like — same JA3 fingerprint, same certificate chain, same handshake timing. There is no proxy signature to fingerprint.</p>
        <h2>Why WireGuard alone isn't enough</h2>
        <p>WireGuard's handshake starts with a fixed magic byte (<code>0x01</code>) and a Noise pattern that's straightforward to fingerprint. Multiple countries auto-throttle or drop it. REALITY solves the fingerprinting problem entirely — Xray still uses fast UDP paths under the hood where possible.</p>
        <h2>How to verify it yourself</h2>
        <p>Run <code>tcpdump -i any host &lt;server_ip&gt;</code> during a connect. You should see a normal TLS 1.3 <code>ClientHello</code> to the decoy SNI, followed by encrypted app-data — no distinct proxy header. The Wireshark JA3 hash matches the decoy CDN's clients exactly.</p>
      </div>
    ),
  },
  { slug: 'mtu-sizing', group: 'Networking & routing', title: 'MTU sizing',
    summary: 'mtu, fragmentation, pmtud, black hole, 1400, 1280',
    body: (
      <div className="legal">
        <h2>Default: 1400</h2>
        <p>Vpnee's TUN interface defaults to MTU 1400 — safe on almost every consumer link (Ethernet 1500 minus REALITY + VLESS overhead). Faster than lowering blindly, doesn't fragment.</p>
        <h2>When to drop to 1280</h2>
        <p>Symptom: pages load but images / large downloads stall (a PMTUD black hole — ICMP "Frag Needed" is being dropped upstream). Settings → Advanced → MTU → 1280. This is the IPv6-mandatory minimum, guaranteed to pass everywhere.</p>
        <h2>Diagnose by hand</h2>
        <p>Ping a large payload with the DF flag: <code>ping -M do -s 1372 8.8.8.8</code>. If 1372 works but 1373 fails, your working payload is 1372 → MTU 1400 (28-byte IP+ICMP overhead). Do the same test through the tunnel to confirm.</p>
      </div>
    ),
  },
  { slug: 'split-tunneling', group: 'Networking & routing', title: 'Split tunneling',
    summary: 'per app, bypass, allow list, block list, tunneled apps',
    body: (
      <div className="legal">
        <h2>Two modes</h2>
        <p><strong>Allow list</strong> — only the listed apps go through Vpnee, everything else uses your real network. <strong>Deny list</strong> — everything goes through Vpnee except the listed apps (useful when your bank app blocks VPN IPs).</p>
        <h2>Android</h2>
        <p>Settings → Split tunnel → pick mode → tap apps to add. Changes take effect on next connect. Requires the tunnel to be off while editing.</p>
        <h2>Windows</h2>
        <p>Settings → Split tunnel → drag executables from Explorer, or click <strong>Add process</strong> and browse. Per-process routing is applied at the Wintun layer.</p>
        <h2>iOS caveat</h2>
        <p>NetworkExtension does not expose per-app split-tunnel to third parties. iOS is device-wide only.</p>
      </div>
    ),
  },
  { slug: 'kill-switch', group: 'Networking & routing', title: 'Kill switch & leaks',
    summary: 'kill switch, dns leak, ipv6, leak test, killswitch, always on',
    body: (
      <div className="legal">
        <h2>Kill switch — on by default</h2>
        <p>Vpnee routes every packet through the tunnel (<code>0.0.0.0/0</code> and <code>::/0</code>). When the tunnel drops, packets stop — nothing leaks to your real network until you either reconnect or explicitly disable the tunnel.</p>
        <h2>Android "Always-on"</h2>
        <p>OS-level guarantee even if the Vpnee process is killed. Settings → Network → VPN → Vpnee → toggle <strong>Always-on VPN</strong> and <strong>Block connections without VPN</strong>. Requires Android 8+.</p>
        <h2>DNS leaks</h2>
        <p>DNS runs inside the tunnel via the app's chosen resolver (default: the connected node's Xray resolver). Test at <a href="https://dnsleaktest.com" target="_blank" rel="noopener">dnsleaktest.com</a> — no ISP DNS should appear.</p>
        <h2>IPv6 policy</h2>
        <p>On Android, IPv6 is off inside the tunnel (badvpn <code>tun2socks</code> is IPv4-only) — safe because <code>::/0</code> is not routed to the TUN interface, so v6 traffic is dropped at the OS level rather than leaking around us. Windows enables full v6 through the Wintun stack.</p>
      </div>
    ),
  },

  // ── Platform features ──────────────────────────────────────────────
  { slug: 'quick-connect-shortcuts', group: 'Platform features', title: 'Quick-connect shortcuts',
    summary: 'tile, widget, deep link, tasker, automation',
    body: (
      <div className="legal">
        <h2>Quick Settings tile</h2>
        <p>Pull down the status bar → tap the Vpnee tile. First time, drag it into your active tiles (bottom drawer, <em>Edit</em> pencil). Subtitle shows uptime + server label when connected, "Off · &lt;plan&gt;" when disconnected. Long-press the tile for a "Force reconnect" action.</p>
        <h2>Home widget</h2>
        <p>Long-press an empty home-screen space → Widgets → Vpnee. Two sizes: compact toggle strip, 4×2 stats panel with live throughput and node label. Auto-refresh on every state change — not capped at Android's 30-minute widget interval.</p>
        <h2>Deep link</h2>
        <p>The scheme <code>vpnee://connect</code> opens the app and starts a connect if the user is logged in. <code>vpnee://disconnect</code> and <code>vpnee://toggle</code> also work. Handy for Tasker, MacroDroid, or Shortcuts routines.</p>
      </div>
    ),
  },
  { slug: 'widgets-notifications', group: 'Platform features', title: 'Widgets & notifications',
    summary: 'notification, foreground, uptime, disconnect, bigtext',
    body: (
      <div className="legal">
        <h2>Ongoing notification</h2>
        <p>While connected, Vpnee shows a persistent notif with the server label, session uptime, live throughput, and a one-tap <strong>Disconnect</strong> action. The <em>bigtext</em> body expands to reveal a full stats block.</p>
        <h2>Event alerts</h2>
        <p>Pushed to your notification tray on: data cap approaching (85%), data cap hit (100%), Pro expiring in 3 days, tunnel dropped mid-session (auto-repair failed), coupon applied. Turn any of these off individually under Settings → Notifications.</p>
        <h2>Silence during focus</h2>
        <p>Vpnee's notification channel respects Do Not Disturb and Focus Modes — the ongoing tunnel notif stays but events are batched until DND lifts.</p>
      </div>
    ),
  },
  { slug: 'auto-repair', group: 'Platform features', title: 'Watchdog & auto-repair',
    summary: 'watchdog, health probe, xray restart, repair, server side',
    body: (
      <div className="legal">
        <h2>Client watchdog</h2>
        <p>The client health-probes the tunnel every 15 s. Failure conditions: no packets for 30 s, echo-request to node timeout, or the Xray process ending unexpectedly. On failure, Xray is restarted and the tunnel re-attached without dropping the OS route.</p>
        <h2>Server-side repair</h2>
        <p>If auto-repair notices your Xray inbound placement is missing on the panel side (e.g. after a server-side rotate), a signed <code>/user/repair</code> call re-provisions your VLESS entry within a couple of seconds.</p>
        <h2>What it doesn't do</h2>
        <p>Auto-repair won't try forever. Three consecutive failures inside 45 s surface a "tunnel unstable" system notification and back off — the app assumes the network itself is broken, not the tunnel.</p>
      </div>
    ),
  },
  { slug: 'battery-optimization', group: 'Platform features', title: 'Battery & background',
    summary: 'battery, doze, oem, miui, coloros, background restriction',
    body: (
      <div className="legal">
        <h2>Standard Android</h2>
        <p>Vpnee runs as a foreground service — the OS gives it CPU without dropping it into Doze. On stock Android (Pixel, Motorola) no tweaks are needed.</p>
        <h2>Aggressive OEM skins</h2>
        <p>MIUI, ColorOS, One UI, Nothing OS, Realme UI aggressively kill background services regardless of foreground state. Add Vpnee to your OEM's "no battery restriction" and "autostart" allow-lists. On MIUI, also disable <em>MIUI Optimization</em> in Developer Options for the widget to update reliably.</p>
        <h2>Windows sleep</h2>
        <p>Vpnee auto-suspends the tunnel when Windows enters modern-standby (S0-Low-Power-Idle) and resumes on wake — no reconnect prompt.</p>
      </div>
    ),
  },
  { slug: 'router-dns', group: 'Platform features', title: 'DNS choices',
    summary: 'dns, dnscrypt, doh, cloudflare, google, custom resolver',
    body: (
      <div className="legal">
        <h2>Default resolver</h2>
        <p>The connected node's local Xray resolver, cached in-tunnel. Fast, no third-party log.</p>
        <h2>Custom DoH endpoint</h2>
        <p>Settings → Advanced → DNS → pick from Cloudflare (1.1.1.1), Google (8.8.8.8), Quad9 (9.9.9.9), or enter a custom DoH URL (<code>https://…/dns-query</code>). DoT is not supported on the client yet — DoH only.</p>
        <h2>Local DNS block-lists</h2>
        <p>Paste a hosts-format block-list URL under Settings → Advanced → DNS → Block-list. Refreshes every 24 h. Common lists: OISD, StevenBlack, AdGuard, HaGeZi.</p>
      </div>
    ),
  },

  // ── Privacy & security ─────────────────────────────────────────────
  { slug: 'privacy-notes', group: 'Privacy & security', title: 'What we see / don\'t see',
    summary: 'privacy, logs, dns, timestamps, fingerprint, anomaly',
    body: (
      <div className="legal">
        <h2>What we can see</h2>
        <p>Your username, email if you gave one, cumulative bytes for plan limits, and a device fingerprint (brand / model / OS version / timezone) used only for anomaly detection during login. That's it.</p>
        <h2>What we do not see</h2>
        <p>Your browsing history, DNS lookups, per-session timestamps, per-domain byte totals, or IP-level metadata. Not stored, not aggregated, not derivable from what we have.</p>
        <h2>Tester program logs</h2>
        <p>If you opt in as a Tester, raw protocol diagnostics are captured while a tester session is active — for debugging Xray, not for surveilling you. Leave any time from Settings → Account → Tester.</p>
        <h2>Legal requests</h2>
        <p>We publish transparency reports twice a year. Requests we've received to date: 0.</p>
      </div>
    ),
  },
  { slug: 'post-quantum', group: 'Privacy & security', title: 'Post-quantum crypto',
    summary: 'mlkem, ml-kem, kyber, post quantum, nist, hnrfl, harvest now',
    body: (
      <div className="legal">
        <h2>ML-KEM VLESS</h2>
        <p>Inside the REALITY handshake, VLESS uses ML-KEM (formerly CRYSTALS-Kyber) key exchange — a NIST-standardised post-quantum KEM. Classical ECDH still runs alongside for compatibility; both must be broken for a session to leak.</p>
        <h2>Why it matters today</h2>
        <p>Harvest-Now-Decrypt-Later. Adversaries record encrypted traffic today, keep it, and hope quantum decryption becomes cheap enough in ~10 years. ML-KEM makes today's capture worthless against a future quantum attack.</p>
        <h2>How to verify</h2>
        <p>Advanced → Diagnostics → Handshake trace. Look for <code>kem=mlkem768</code> in the negotiated parameters line. If it shows <code>kem=none</code>, the server has post-quantum turned off (older nodes).</p>
      </div>
    ),
  },
  { slug: 'device-management', group: 'Privacy & security', title: 'Devices & sessions',
    summary: 'devices, sessions, revoke, force logout, slot policy',
    body: (
      <div className="legal">
        <h2>Session slots</h2>
        <p>Free: 1 device. Gold: 3. Platinum: 5. When you exceed the slot, the oldest inactive device is signed out automatically (rather than blocking the new device).</p>
        <h2>See active devices</h2>
        <p>Account → Devices. Shows platform, city (from IP), first-seen and last-active timestamps. Tap <strong>Revoke</strong> to force-sign-out a device — takes effect within 60 s.</p>
        <h2>Revoke everything</h2>
        <p>Account → Security → <strong>Sign out all sessions</strong> nukes every token including the one you're on. Useful after a lost phone.</p>
      </div>
    ),
  },

  // ── Billing & plans ────────────────────────────────────────────────
  { slug: 'coupons', group: 'Billing & plans', title: 'Coupons & promo codes',
    summary: 'coupon, promo, discount, free days, gold, upgrade',
    body: (
      <div className="legal">
        <h2>Two coupon types</h2>
        <p><strong>Discount</strong> — percentage off your next upgrade. Enter during Pro checkout; the discounted amount is what actually charges.</p>
        <p><strong>Free days</strong> — instant N-day Gold access without payment. No renewal, no card required; access lifts automatically after the trial ends.</p>
        <h2>How to enter one</h2>
        <p>Home → Menu → Upgrade to Pro → Promo Code field. Codes are case-insensitive. Errors show in-line — no need to retry blindly.</p>
        <h2>Site-wide auto-apply</h2>
        <p>When a global discount is live, it auto-applies at checkout — you'll see the discounted amount without pasting anything. See <Link to="/pricing">the pricing page</Link>; a live promo shows a ticket at the top.</p>
      </div>
    ),
  },
  { slug: 'data-allowance', group: 'Billing & plans', title: 'Data allowance',
    summary: 'data cap, throttle, quota, monthly, allowance, reset',
    body: (
      <div className="legal">
        <h2>Cumulative counter</h2>
        <p>Only cumulative bytes per account are counted for plan limits — no per-session or per-destination totals. Free: 5 GB/mo. Gold: 50 GB/mo. Platinum: unlimited.</p>
        <h2>What happens at 100%</h2>
        <p>The tunnel keeps working — throughput throttles hard (typically ~256 kbps). Emails, chat, and small pages still load; video calls degrade. You get a system notification at 85% and at 100%. Upgrade in-app and the limit lifts instantly.</p>
        <h2>Reset day</h2>
        <p>Rolls over on the calendar day matching your plan start date each month. Manual reset available to admins on request.</p>
      </div>
    ),
  },
  { slug: 'refunds', group: 'Billing & plans', title: 'Refunds & disputes',
    summary: 'refund, chargeback, cancel, prorated, billing',
    body: (
      <div className="legal">
        <h2>7-day window</h2>
        <p>Any annual plan is refundable within 7 days of purchase if less than 5% of the plan's monthly data has been consumed. Email <a href="mailto:billing@tnidu.com">billing@tnidu.com</a>.</p>
        <h2>Beyond 7 days</h2>
        <p>Prorated refunds are considered case-by-case (rare, but yes on genuine service outages that lasted &gt;24 h in aggregate).</p>
        <h2>Chargebacks</h2>
        <p>Please email first before filing with your bank — 95% of "billing errors" turn out to be a coupon that didn't paste cleanly and we can fix in a few minutes.</p>
      </div>
    ),
  },

  // ── Troubleshooting ────────────────────────────────────────────────
  { slug: 'troubleshooting', group: 'Troubleshooting', title: 'General troubleshooting',
    summary: 'connected but nothing loads, drops, widget, slow, stuck',
    body: (
      <div className="legal">
        <h2>Connected but nothing loads</h2>
        <p>Almost always MTU. The app tunes to 1400 by default; if you're on a network with a smaller MTU (some cellular carriers, most enterprise VPNs), drop to 1280 in Settings → Advanced. See <Link to="/docs/mtu-sizing">MTU sizing</Link> to diagnose exactly.</p>
        <h2>Repeated drops</h2>
        <p>Turn on <strong>Aggressive keepalive</strong> in Settings. If drops persist, share the tunnel log (Menu → Send log to support). Include your ISP and the approximate time of the drops.</p>
        <h2>Widget not updating</h2>
        <p>Some OEMs (MIUI, ColorOS, One UI) kill background services aggressively. Add Vpnee to the "no battery restriction" list — see <Link to="/docs/battery-optimization">Battery & background</Link>.</p>
        <h2>Slow speeds</h2>
        <p>Run the <a href="https://fast.com" target="_blank" rel="noopener">Fast.com</a> or <a href="https://speed.cloudflare.com" target="_blank" rel="noopener">speed.cloudflare.com</a> test with the tunnel on and again with it off. Vpnee typically hits 60–85% of your raw speed. If you're seeing under 30%, try a different city or drop MTU.</p>
      </div>
    ),
  },
  { slug: 'connect-fails', group: 'Troubleshooting', title: 'Connect fails',
    summary: 'connect, handshake, timeout, cannot connect, gfw, censor',
    body: (
      <div className="legal">
        <h2>Handshake timeout</h2>
        <p>The client dials but never completes the REALITY handshake. Usual causes: the local network blocks TLS 1.3 to the decoy SNI, or your account was pushed to a node that's currently under load.</p>
        <p>Try: (1) flip <strong>Anti-DPI / TLS fragmentation</strong> in Settings, (2) pick a different city manually (Pro), (3) retry from a different network / hotspot as a control.</p>
        <h2>"No route to host"</h2>
        <p>Almost always OS-level. Reset the tunnel service: Settings → About → <strong>Reset network stack</strong>. On Windows this reinstalls the Wintun adapter; on Android it clears the OS VPN state.</p>
        <h2>Aggressive GFW / captive networks</h2>
        <p>Airport / hotel captive portals must be logged into <em>before</em> Vpnee is on. Toggle Vpnee off, complete the portal in your browser, then toggle it back on.</p>
      </div>
    ),
  },
  { slug: 'debug-logs', group: 'Troubleshooting', title: 'Debug logs & tester mode',
    summary: 'logs, debug, xray log, tester, live logs',
    body: (
      <div className="legal">
        <h2>Send-log-to-support</h2>
        <p>Menu → Support → <strong>Send debug log</strong>. Uploads the last 5 minutes of tunnel logs + client state. Automatically stripped of your VLESS UUID, session tokens, and payment metadata. A ticket ID is shown — reply from the same account and we can pull it up instantly.</p>
        <h2>Tester program</h2>
        <p>Sign up under Account → Tester. Grants free Platinum access for the tester duration in exchange for the app streaming Xray protocol traces to us during your active sessions. You can leave at any time — the trace stream stops immediately.</p>
        <h2>Live tunnel logs (Windows)</h2>
        <p>Settings → About → <strong>Open log folder</strong>. Tail <code>xray.log</code> for real-time protocol messages. Redact your session UUID before sharing publicly.</p>
      </div>
    ),
  },

];

const DOCS: Record<string, Doc> = Object.fromEntries(DOCS_LIST.map(d => [d.slug, d]));

const GROUPS: { title: string; slugs: string[] }[] = (() => {
  const map = new Map<string, string[]>();
  DOCS_LIST.forEach(d => { const a = map.get(d.group) || []; a.push(d.slug); map.set(d.group, a); });
  return [...map.entries()].map(([title, slugs]) => ({ title, slugs }));
})();

export default function Docs() {
  const { slug } = useParams<{ slug?: string }>();
  const active = slug && DOCS[slug] ? slug : 'getting-started';
  const doc = DOCS[active];

  const [q, setQ] = useState('');
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return null;
    return DOCS_LIST
      .filter(d => (d.title + ' ' + d.summary).toLowerCase().includes(needle))
      .map(d => d.slug);
  }, [q]);

  const flatOrder = DOCS_LIST.map(d => d.slug);
  const idx = flatOrder.indexOf(active);
  const prev = idx > 0 ? DOCS[flatOrder[idx - 1]] : null;
  const next = idx < flatOrder.length - 1 ? DOCS[flatOrder[idx + 1]] : null;

  return (
    <Section chip="Documentation" title={doc.title} subtitle={doc.summary}>
      <div className="grid gap-6 lg:gap-8 lg:grid-cols-[240px_1fr]">
        <aside className="lg:sticky lg:top-24 h-fit space-y-3">
          <div className="glass p-2">
            <div className="relative">
              <input
                type="search"
                placeholder="Search docs…"
                value={q}
                onChange={e => setQ(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 pl-8 text-sm outline-none focus:border-[color:var(--color-brand)]"
              />
              <svg aria-hidden width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[color:var(--color-ink-faint)]"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            </div>
          </div>
          <nav className="glass p-2 flex flex-col gap-3 max-h-[70vh] overflow-y-auto">
            {GROUPS.map(g => {
              const groupSlugs = matches ? g.slugs.filter(s => matches.includes(s)) : g.slugs;
              if (groupSlugs.length === 0) return null;
              return (
                <div key={g.title}>
                  <div className="px-3 pt-1 pb-1.5 text-[10px] uppercase tracking-[0.16em] text-[color:var(--color-ink-faint)] font-bold">{g.title}</div>
                  <div className="flex flex-col gap-1">
                    {groupSlugs.map(s => (
                      <NavLink
                        key={s}
                        to={`/docs/${s}`}
                        className={({ isActive }) =>
                          `px-3 py-2 rounded-lg text-sm font-medium transition ${
                            isActive || (s === active && !slug)
                              ? 'text-white bg-white/5'
                              : 'text-[color:var(--color-ink-dim)] hover:text-white hover:bg-white/5'
                          }`
                        }
                      >
                        {DOCS[s].title}
                      </NavLink>
                    ))}
                  </div>
                </div>
              );
            })}
            {matches && matches.length === 0 && (
              <div className="px-3 py-4 text-xs text-[color:var(--color-ink-faint)]">No matches. Try <Link to="/help" className="underline">Help</Link>.</div>
            )}
          </nav>
        </aside>

        <div className="min-w-0">
          <article className="glass p-6 sm:p-8 md:p-10">{doc.body}</article>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {prev ? (
              <Link to={`/docs/${prev.slug}`} className="glass p-4 flex flex-col gap-1 hover:border-[color:var(--color-brand)]/40 transition">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--color-ink-faint)] inline-flex items-center gap-1"><IconArrowLeft width={10} height={10}/> Previous</span>
                <span className="text-sm font-semibold">{prev.title}</span>
              </Link>
            ) : <span />}
            {next ? (
              <Link to={`/docs/${next.slug}`} className="glass p-4 flex flex-col gap-1 text-right hover:border-[color:var(--color-brand)]/40 transition">
                <span className="text-[10px] uppercase tracking-widest text-[color:var(--color-ink-faint)]">Next →</span>
                <span className="text-sm font-semibold">{next.title}</span>
              </Link>
            ) : <span />}
          </div>

          <div className="mt-6 glass p-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1">
              <div className="font-semibold text-sm">Can't find what you need?</div>
              <div className="text-xs text-[color:var(--color-ink-dim)] mt-0.5">Support chat is on the Help page. A human replies within a few hours on weekdays.</div>
            </div>
            <Link to="/help" className="btn btn-primary shrink-0 justify-center">Open Help</Link>
          </div>
        </div>
      </div>
    </Section>
  );
}
